// ============================================================
// TERROR — START MENU  (Pokémon-style front door)
// ------------------------------------------------------------
// A single nested menu that fronts every interactable in the hub:
// Battle, Collection, Shop, Help, Settings. Select a category and
// its items fan out (root stays uncluttered — 4xS Simple). Each
// leaf just fires a callback the host supplies, so this component
// is presentation-only and never has to know how navigation works.
//
// GILDED OBSIDIAN v2 (July 5, 2026) — the material re-forge:
// the flat vellum card became a faceted obsidian slab set in a
// forged-gold metal frame. Active rows LIGHT UP (lit slab, molten
// side bar, gold edge, text glow); idle rows recede into the dark.
// Amethyst stardust drifts in the glass. Local GM copy is DEAD —
// palette imports from theme.js v4 (one source of truth).
// Behavior, props, and keyboard handling are byte-for-byte v1.
//
// ── DRIVEN BY A `sections` CONFIG ───────────────────────────
//   sections = [
//     { id, label, icon, items: [ { id, label, run: () => {...} } ] },
//     ...
//   ]
// Selecting a category drills into it; selecting a leaf calls
// onClose() then item.run(). Back/Esc steps out, then closes.
//
// ── INPUT ───────────────────────────────────────────────────
//   ↑/↓        move highlight
//   →/Q/Enter  select (drill into a category, or fire a leaf)
//   ←/Esc/⌫    back out (then close at the root)
//   click/tap  selects a row directly (mobile-friendly)
//
// A short ready-delay ignores the very first key so the keypress
// that opened the menu can't instantly select the top row.
// ============================================================

import { useState, useEffect, useRef, useCallback } from "react";
import {
  GM, GOLD_METAL, GOLD_METAL_H,
  OBSIDIAN_PANEL, OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT,
  facet, facetTB, textGlow, TEXT_SET,
} from "./theme";
import { StarDust } from "./TerrorUI"; // also guarantees the twinkle keyframes are injected

export default function StartMenu({ sections = [], onClose }) {
  const [section, setSection] = useState(null); // null = root (category list)
  const [idx, setIdx] = useState(0);
  const readyRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => { readyRef.current = true; }, 320);
    return () => clearTimeout(t);
  }, []);

  const atRoot = section === null;
  const list = atRoot ? sections : (section.items || []);

  const back = useCallback(() => {
    if (section) { setSection(null); setIdx(0); }
    else { onClose && onClose(); }
  }, [section, onClose]);

  // Fire the item at position i (drill into a category or run a leaf).
  const activate = useCallback((i) => {
    const cur = list[i];
    if (!cur) return;
    if (atRoot) { setSection(cur); setIdx(0); }
    else { onClose && onClose(); cur.run && cur.run(); }
  }, [list, atRoot, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (!readyRef.current) return;
      const len = list.length;
      if (e.key === "ArrowUp")        { e.preventDefault(); setIdx(i => (i - 1 + len) % len); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setIdx(i => (i + 1) % len); }
      else if (e.key === "ArrowRight" || e.key === "Enter" || e.key === "q" || e.key === "Q") {
        e.preventDefault(); activate(idx);
      }
      else if (e.key === "ArrowLeft" || e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault(); back();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [list, idx, activate, back]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "radial-gradient(ellipse at 50% 40%, rgba(23,15,48,0.82) 0%, rgba(4,2,10,0.9) 100%)",
        backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)",
        padding: 16,
      }}
    >
      {/* forged-gold frame — the metal is the wrapper; clip-path kills
          box-shadow, so outer depth rides on filter: drop-shadow */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "min(86vw, 336px)",
          padding: 3,
          clipPath: facet(16),
          background: GOLD_METAL,
          filter: "drop-shadow(0 18px 46px rgba(0,0,0,0.8)) drop-shadow(0 0 22px rgba(232,184,75,0.22))",
        }}
      >
        {/* obsidian slab — the shell; the row list scrolls INSIDE it
            so the frame never scrolls away (shell + inner-scroll) */}
        <div style={{
          position: "relative",
          clipPath: facet(14),
          background: OBSIDIAN_PANEL,
          padding: "18px 16px 12px",
          fontFamily: GM.font,
          overflow: "hidden",
        }}>
          {/* crystalline sheen + drifting stardust in the glass */}
          <div style={{ position: "absolute", inset: 0, background: "repeating-linear-gradient(135deg, transparent 0px, transparent 23px, rgba(192,132,252,0.035) 23px, rgba(192,132,252,0.035) 24px)", pointerEvents: "none" }} />
          <StarDust count={14} />
          {/* top light — the seam where the frame catches the void */}
          <div style={{ position: "absolute", top: 0, left: "14%", right: "14%", height: 1, background: "linear-gradient(90deg, transparent, rgba(245,216,120,0.75), transparent)", pointerEvents: "none" }} />

          {/* Header */}
          <div style={{
            position: "relative",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: 6,
          }}>
            <span style={{ color: GM.amethystBright, fontSize: 11, textShadow: `0 0 7px ${GM.amethystBright}` }}>✦</span>
            <span style={{
              color: "#f0cc60", fontSize: 16, fontWeight: 800,
              letterSpacing: 5, textTransform: "uppercase",
              textShadow: textGlow(GM.inkGoldBright),
            }}>
              {atRoot ? "Menu" : section.label}
            </span>
            <button
              onClick={atRoot ? onClose : back}
              aria-label={atRoot ? "Close" : "Back"}
              style={{
                width: 27, height: 27,
                background: OBSIDIAN_SLAB,
                border: `1px solid ${GM.inkBrown}`, borderRadius: 7,
                boxShadow: "inset 0 1px 0 rgba(226,232,244,0.18), 0 2px 6px rgba(0,0,0,0.6)",
                color: GM.silver, fontSize: 13, lineHeight: 1, cursor: "pointer",
              }}
            >
              {atRoot ? "×" : "‹"}
            </button>
          </div>
          {/* metal rule under the title */}
          <div style={{ position: "relative", height: 2, margin: "0 auto 14px", width: "62%", background: GOLD_METAL_H, boxShadow: `0 0 8px ${GM.inkGoldBright}55` }} />

          {/* Rows — faceted obsidian shards; the active one EMITS */}
          <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 9, maxHeight: "58vh", overflowY: "auto", paddingBottom: 2 }}>
            {list.map((row, i) => {
              const active = i === idx;
              return (
                <button
                  key={row.id || i}
                  onClick={() => activate(i)}
                  onMouseEnter={() => setIdx(i)}
                  style={{
                    position: "relative",
                    display: "flex", alignItems: "center", gap: 10,
                    width: "100%", textAlign: "left",
                    padding: "13px 14px",
                    border: "none",
                    borderTop: active ? "1px solid #f5d878" : "1px solid transparent",
                    clipPath: facetTB(10),
                    background: active ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
                    boxShadow: active
                      ? `inset 0 1px 0 rgba(245,216,120,0.55), inset 0 0 24px ${GM.amethyst}3d, inset 0 -8px 16px rgba(0,0,0,0.4)`
                      : "inset 0 1px 0 rgba(226,232,244,0.14), inset 0 -6px 14px rgba(0,0,0,0.45)",
                    filter: active ? `drop-shadow(0 0 9px ${GM.inkGoldBright}44)` : "drop-shadow(0 3px 8px rgba(0,0,0,0.55))",
                    color: active ? "#ffe9a8" : "#cfc4e6",
                    fontFamily: GM.font, fontSize: 14, fontWeight: active ? 700 : 500,
                    letterSpacing: 2, cursor: "pointer",
                    textShadow: active ? textGlow(GM.inkGoldBright) : TEXT_SET,
                    transition: "all 0.15s ease",
                  }}
                >
                  {active && (
                    <span style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: 3, background: GOLD_METAL, boxShadow: `0 0 8px ${GM.inkGoldBright}e0` }} />
                  )}
                  <span style={{
                    width: 14, color: "#f5d878",
                    opacity: active ? 1 : 0, fontSize: 13,
                    textShadow: `0 0 6px ${GM.inkGoldBright}`,
                  }}>▸</span>
                  {row.icon && (
                    <span style={{ fontSize: 15, width: 18, textAlign: "center", filter: active ? `drop-shadow(0 0 4px ${GM.inkGoldBright}88)` : "none" }}>{row.icon}</span>
                  )}
                  <span style={{ flex: 1 }}>{row.label}</span>
                  {atRoot && (
                    <span style={{ color: active ? GM.amethystBright : GM.vellumFaint, fontSize: 13, textShadow: active ? `0 0 6px ${GM.amethystBright}` : "none" }}>›</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer hint */}
          <div style={{
            position: "relative",
            marginTop: 12, textAlign: "center",
            color: GM.vellumFaint, fontSize: 9, letterSpacing: 2,
            textShadow: TEXT_SET,
          }}>
            ↑↓ MOVE · Q/ENTER SELECT · ESC BACK
          </div>
        </div>
      </div>
    </div>
  );
}
